"use client";

import { useEffect, useLayoutEffect } from "react";

import type { PublicPageBrand } from "@/lib/public-brand-theme";
import {
  ROMA_FUNNEL_DATA_ATTR,
  ROMA_FUNNEL_ON_VALUE,
  shouldApplyRomaDarkFunnel,
} from "@/lib/roma-funnel-tokens";

type RomaDarkThemeProps = {
  pageBrand: PublicPageBrand;
  /** Server-resolved kill switch — authoritative over client-inlined env. */
  romaFunnelEnabled?: boolean;
};

function applyDocumentDarkTheme() {
  const root = document.documentElement;
  root.classList.remove("light");
  root.classList.add("dark");
  root.style.colorScheme = "dark";
}

function clearDocumentDarkTheme() {
  const root = document.documentElement;
  root.classList.remove("dark");
  root.classList.add("light");
  root.style.colorScheme = "light";
}

function resolveFunnelEnabled(
  pageBrand: PublicPageBrand,
  romaFunnelEnabled?: boolean,
): boolean {
  if (typeof romaFunnelEnabled === "boolean") {
    return pageBrand === "chefs-roma" && romaFunnelEnabled;
  }

  return shouldApplyRomaDarkFunnel(pageBrand);
}

/**
 * Syncs `data-roma-funnel="on"` on the public page shell when the kill switch
 * and chefs-roma brand both match. Layout also sets the attribute server-side
 * for first paint; this keeps client navigations consistent.
 */
export function RomaDarkTheme({
  pageBrand,
  romaFunnelEnabled,
}: RomaDarkThemeProps) {
  const enabled = resolveFunnelEnabled(pageBrand, romaFunnelEnabled);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    applyDocumentDarkTheme();

    return () => {
      clearDocumentDarkTheme();
    };
  }, [enabled]);

  useEffect(() => {
    const shell = document.querySelector<HTMLElement>(
      `[data-public-page-brand="${pageBrand}"]`,
    );
    if (!shell) return;

    if (enabled) {
      shell.setAttribute(ROMA_FUNNEL_DATA_ATTR, ROMA_FUNNEL_ON_VALUE);
      return () => {
        shell.removeAttribute(ROMA_FUNNEL_DATA_ATTR);
      };
    }

    shell.removeAttribute(ROMA_FUNNEL_DATA_ATTR);
  }, [enabled, pageBrand]);

  return null;
}
